import { Box, Button, Grid2, Typography, useTheme } from "@mui/material";
import {
  AddLocation,
  Check,
  DirectionsCar,
  GroupAdd,
  RoomService,
  SupervisorAccountOutlined,
  ThumbUp
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { NotificationCard } from "./NotificationSubjectCard.partial";
import ApprovalLocationHierarchy from "./ApprovalLocationHierarchy.partial";
import {
  NotificationDataType,
  NotificationSettingOptions
} from "../Domain/NotificationSettings.types";
import type { ServiceHierarchyNode } from "../Domain/serviceNotificationHierarchy.types";
import { Dispatch, type ReactElement, type ReactNode, SetStateAction } from "react";

type NotificationCardConfig = {
  title: string;
  icon: ReactElement;
  options: NotificationSettingOptions[];
  timeControls?: boolean;
  appendContent?: ReactNode;
};

type P = {
  notificationOptions: {
    bookingOptions: NotificationSettingOptions[];
    approvalOptions: NotificationSettingOptions[];
    supervisorOptions: NotificationSettingOptions[];
    serviceOptions: NotificationSettingOptions[];
    connectionOptions: NotificationSettingOptions[];
    fleetOptions: NotificationSettingOptions[];
  };
  switches: Record<string, boolean>;
  setSwitches: Dispatch<SetStateAction<Record<string, boolean>>>;
  timeSwitches: Record<string, number>;
  setTimeSwitches: Dispatch<SetStateAction<Record<string, number>>>;
  handleUpdateSettings: () => void;
  approvalHierarchyRoot: ServiceHierarchyNode | null;
  approvalLeafEnabled: Record<string, boolean>;
  setApprovalLeafEnabled: Dispatch<SetStateAction<Record<string, boolean>>>;
};

export default function NotificationCardList({
  notificationOptions,
  switches,
  setSwitches,
  timeSwitches,
  setTimeSwitches,
  handleUpdateSettings,
  approvalHierarchyRoot,
  approvalLeafEnabled,
  setApprovalLeafEnabled
}: Readonly<P>) {
  const { t } = useTranslation();
  const theme = useTheme();
  const iconColor = theme.palette.mode === "light" ? "black" : "white";

  const {
    bookingOptions,
    approvalOptions,
    supervisorOptions,
    serviceOptions,
    connectionOptions,
    fleetOptions
  } = notificationOptions;

  // show service location-floor-room hierarchy when booking approval admin notification is on
  const showApprovalLocationHierarchy =
    switches[NotificationDataType.BOOKING_APPROVAL_ADMIN] === true;

  /** card items of notification options */
  const notificationCards: NotificationCardConfig[] = [
    {
      title: "Booking",
      icon: <AddLocation sx={{ color: iconColor }} />,
      options: bookingOptions,
      timeControls: true // custom flag for time switches
    },
    {
      title: "Approval",
      icon: <ThumbUp sx={{ color: iconColor }} />,
      options: approvalOptions,
      ...(showApprovalLocationHierarchy && approvalHierarchyRoot
        ? {
            appendContent: (
              <ApprovalLocationHierarchy
                hierarchyRoot={approvalHierarchyRoot}
                enabled={approvalLeafEnabled}
                setEnabled={setApprovalLeafEnabled}
              />
            )
          }
        : {})
    },
    {
      title: "Supervisor",
      icon: <SupervisorAccountOutlined sx={{ color: iconColor }} />,
      options: supervisorOptions
    },
    {
      title: "Connections",
      icon: <GroupAdd sx={{ color: iconColor }} />,
      options: connectionOptions
    },
    {
      title: "Service",
      icon: <RoomService sx={{ color: iconColor }} />,
      options: serviceOptions
    },
    {
      title: "Drawer_Fleet",
      icon: <DirectionsCar sx={{ color: iconColor }} />,
      options: fleetOptions,
      timeControls: true // custom flag for time switches
    }
  ];

  return (
    <Box sx={{ width: "100%" }}>
      <Grid2 container sx={{ width: "100%", justifyContent: "space-between", mb: 2 }}>
        <Grid2>
          <Typography variant="h6" gutterBottom>
            {t("NotificationSettingSubHeader")}
          </Typography>
        </Grid2>

        <Grid2>
          <Button onClick={handleUpdateSettings} data-testid="handle-update-settings-btn">
            <Check sx={{ mr: 1 }} /> {t("Save")}
          </Button>
        </Grid2>
      </Grid2>

      {notificationCards.map((card, index) => (
        <NotificationCard
          key={`${card.title}(${index})`}
          title={card.title}
          icon={card.icon}
          color={theme.palette.background.paper}
          items={card.options}
          switches={switches}
          setSwitches={setSwitches}
          {...(card.timeControls ? { timeSwitches, setTimeSwitches } : {})}
          {...(card.appendContent === undefined ? {} : { appendContent: card.appendContent })}
        />
      ))}
    </Box>
  );
}
